import styled from 'foundations-theming/utils';
import { useEffect, useState, useContext } from 'react';
import { BUTTON_RADIUS } from 'HubStyleTokens/sizes';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import FilterDescriptionInput from 'automation-ui-enrollment/components/aiAssistant/FilterDescriptionInput';
import LabelAndButton from 'automation-ui-enrollment/components/aiAssistant/LabelAndButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import { H5 } from 'UIComponents/elements/headings';
import ContentAssistanceTooltip from 'automation-ui-enrollment/components/aiAssistant/ContentAssistanceTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FilterGroupContainer = styled.div.withConfig({
  displayName: "FilterDescriptionForms__FilterGroupContainer",
  componentId: "sc-1vkwkqy-0"
})(["background-color:", ";border:1px solid ", ";border-radius:", ";padding:12px;"], ({
  theme
}) => theme.color['do-not-use-gypsum'], ({
  theme
}) => theme.color['do-not-use-battleship'], BUTTON_RADIUS);
const removeEmptyFilterInputs = filterGroups => {
  return filterGroups
  // create new filter groups removing filter inputs with no value
  .map(fg => {
    return {
      filterInputs: fg.filterInputs.filter(fi => !!fi)
    };
  })
  // create new orFilterGroups with no empty filter groups (all filter inputs are empty)
  .filter(fg => fg.filterInputs.length > 0 && fg.filterInputs.filter(fi => fi !== '').length > 0);
};
const FilterDescriptionForms = ({
  filterGroups,
  hasError = false,
  inputsDisabled = false,
  isContentAssistanceEnabled = true,
  hasGeneratedFilter = false,
  onFiltersUpdated,
  readOnly = false,
  screen
}) => {
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const [orFilterGroups, setOrFilterGroups] = useState(filterGroups || [{
    filterInputs: ['']
  }]);
  const [areAddFilterButtonsEnabled, setAreAddFilterButtonsEnabled] = useState(false);
  useEffect(() => {
    // the add filter group button will be enabled when any filter input has a value
    // or there is more than one filter group
    setAreAddFilterButtonsEnabled(orFilterGroups.length > 1 || orFilterGroups.reduce((agg, fg) => {
      return agg || fg.filterInputs.filter(fi => fi !== '').length > 0;
    }, false));
  }, [orFilterGroups]);
  const handleOnChangeInput = (orGroupIndex, filterInputIndex) => value => {
    const updatedOrFilterGroups = [...orFilterGroups];
    updatedOrFilterGroups[orGroupIndex].filterInputs[filterInputIndex] = value;
    setOrFilterGroups(updatedOrFilterGroups);
    if (onFiltersUpdated) {
      // the consuming component should only be concerned with filter groups and inputs that have values
      onFiltersUpdated(removeEmptyFilterInputs(updatedOrFilterGroups));
    }
  };
  const handleOnRemoveInput = (orGroupIndex, filterInputIndex) => () => {
    const updatedOrFilterGroups = orFilterGroups
    // create new filter groups removing the filter input associated with the remove click
    .map((fg, fgIndex) => {
      if (fgIndex === orGroupIndex) {
        return {
          filterInputs: fg.filterInputs.filter((fi, fIndex) => {
            return fIndex !== filterInputIndex;
          })
        };
      }
      return fg;
    })
    // remove filter groups that now contain no inputs
    .filter((fg, fgIndex) => {
      if (orGroupIndex === fgIndex) {
        return fg.filterInputs.length > 0;
      }
      return true;
    });
    setOrFilterGroups(updatedOrFilterGroups);
    trackEvent(usageTracker, Trackables.CLICK_TO_REMOVE_FILTER_FROM_PROMPT, {
      screen,
      mode: hasGeneratedFilter ? 're-generating' : 'generating',
      type: updatedOrFilterGroups.length === orFilterGroups.length ? 'AND' : 'OR'
    });
    if (onFiltersUpdated) {
      onFiltersUpdated(removeEmptyFilterInputs(updatedOrFilterGroups));
    }
  };
  const handleOnAddFilter = orGroupIndex => () => {
    const updatedOrFilterGroups = [...orFilterGroups];
    const {
      filterInputs
    } = updatedOrFilterGroups[orGroupIndex];
    trackEvent(usageTracker, Trackables.CLICK_TO_ADD_FILTER_TO_PROMPT, {
      screen,
      mode: hasGeneratedFilter ? 're-generating' : 'generating',
      type: 'AND'
    });
    filterInputs.push('');
    setOrFilterGroups(updatedOrFilterGroups);
  };
  const handleOnAddFilterGroup = () => {
    trackEvent(usageTracker, Trackables.CLICK_TO_ADD_FILTER_TO_PROMPT, {
      screen,
      mode: hasGeneratedFilter ? 're-generating' : 'generating',
      type: 'OR'
    });
    const updatedOrFilterGroups = [...orFilterGroups, {
      filterInputs: ['']
    }];
    setOrFilterGroups(updatedOrFilterGroups);
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [orFilterGroups.map(({
      filterInputs
    }, orGroupIndex) => {
      const displayOrLabelBetweenGroups = orGroupIndex !== orFilterGroups.length - 1;
      return /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsxs(FilterGroupContainer, {
          children: [/*#__PURE__*/_jsx(H5, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.aiAssistantFilterDescription.filterForm.groupTitle",
              options: {
                count: orGroupIndex + 1
              }
            })
          }), filterInputs.map((filterInputValue, filterInputIndex) => /*#__PURE__*/_jsx(FilterDescriptionInput, {
            autoFocus: true,
            disabled: inputsDisabled || !isContentAssistanceEnabled,
            hasError: hasError,
            labelDisplayed: filterInputIndex !== 0,
            onChange: handleOnChangeInput(orGroupIndex, filterInputIndex),
            onRemove: handleOnRemoveInput(orGroupIndex, filterInputIndex),
            readOnly: readOnly,
            removeDisplayed: filterInputIndex !== 0 || orGroupIndex !== 0,
            value: filterInputValue
          }, `${orGroupIndex}-${filterInputIndex}`)), /*#__PURE__*/_jsx(LabelAndButton, {
            Button: /*#__PURE__*/_jsx(ContentAssistanceTooltip, {
              disabled: !inputsDisabled && isContentAssistanceEnabled,
              children: /*#__PURE__*/_jsxs(UIButton, {
                disabled: inputsDisabled || readOnly || !isContentAssistanceEnabled || !areAddFilterButtonsEnabled,
                onClick: handleOnAddFilter(orGroupIndex),
                size: "xs",
                use: "tertiary-light",
                children: [/*#__PURE__*/_jsx(UIIcon, {
                  name: "add"
                }), /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "automation-ui-enrollment.aiAssistantFilterDescription.filterForm.addFilterButton"
                })]
              })
            }),
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.aiAssistantFilterDescription.filterForm.labels.and"
            }),
            wrapperClassName: "m-top-3"
          })]
        }), displayOrLabelBetweenGroups && /*#__PURE__*/_jsx(LabelAndButton, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.aiAssistantFilterDescription.filterForm.labels.or"
          }),
          wrapperClassName: "m-y-2"
        })]
      }, orGroupIndex);
    }), /*#__PURE__*/_jsx(LabelAndButton, {
      Button: /*#__PURE__*/_jsx(ContentAssistanceTooltip, {
        disabled: !inputsDisabled && isContentAssistanceEnabled,
        children: /*#__PURE__*/_jsxs(UIButton, {
          onClick: handleOnAddFilterGroup,
          disabled: readOnly || inputsDisabled || !isContentAssistanceEnabled || !areAddFilterButtonsEnabled,
          size: "xs",
          use: "tertiary-light",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "add"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.aiAssistantFilterDescription.filterForm.addFilterGroupButton"
          })]
        })
      }),
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.aiAssistantFilterDescription.filterForm.labels.or"
      }),
      wrapperClassName: "m-top-3"
    })]
  });
};
export default FilterDescriptionForms;